import { Button, Flex, Heading, Text } from '@chakra-ui/react'
import KalviumSdk from '@kalvi-education/kalvium-sdk'
import { useQuery } from '@tanstack/react-query'
import { Field, useFormikContext } from 'formik'
import { NumberInputControl, PinInputControl } from 'formik-chakra-ui'
import { useEffect, useState } from 'react'

import { defaultBorder } from '../../../../../chakraTheme'
import useCountDown from '../../../../../hooks/useCountDown/useCountDown'
import { countryCodes } from '../../../../../utils/countryCodes'
import FormikSelect from '../FormikSelect'

const resendTimeout = 60000
const VerifyMobileForm = ({
    title,
    onMobileChange,
    onOtpChange,
    onOtpVerified,
}) => {
    const { values, setFieldValue } = useFormikContext()
    const [remTime, timerActions] = useCountDown(resendTimeout, 1000) // remTime => remaining time in milliseconds | timerActions => {start | pause | resume | reset}
    const [shouldVerifyOtp, setShouldVerifyOtp] = useState(false)

    const {
        isError: isOtpVerifyError,
        error: otpVerifyError,
        isSuccess: isOtpVerifySuccess,
        refetch: verifyOtp,
    } = useQuery({
        queryKey: [
            'otp-verify',
            { otp: values.otp, mobile: values.countryCode + values.mobile },
        ],
        queryFn: async queryData => {
            return KalviumSdk.services.admission.verifyOtp({
                mobile: queryData.queryKey[1].mobile,
                otp: queryData.queryKey[1].otp,
            })
        },
        enabled: false,
        retry: false,
        staleTime: 0,
        gcTime: 0,
    })

    const {
        data: otpSendData,
        error: otpSendError,
        isError: isOtpSendError,
        refetch: sendOtp,
        isSuccess: isOtpSendSuccess,
    } = useQuery({
        queryKey: ['otp-send', { mobile: values.countryCode + values.mobile }],
        queryFn: async queryData => {
            return KalviumSdk.services.admission.sendOtp({
                mobile: queryData.queryKey[1].mobile,
            })
        },
        enabled: false,
        retry: false,
        staleTime: 0,
        gcTime: 0,
    })

    useEffect(() => {
        onOtpChange(values.otp)
        setShouldVerifyOtp(false)
    }, [values.otp])

    useEffect(() => {
        onMobileChange(values.countryCode + values.mobile)
        setFieldValue('otp', '')
        setShouldVerifyOtp(false)
        timerActions.reset()
    }, [values.countryCode, values.mobile])

    useEffect(() => {
        if (otpSendData) {
            timerActions.start(otpSendData.remaining_time * 1000)
        }
    }, [otpSendData])

    useEffect(() => {
        if (shouldVerifyOtp) verifyOtp()
    }, [shouldVerifyOtp])

    useEffect(() => {
        if (isOtpVerifySuccess)
            onOtpVerified(values.countryCode + values.mobile, values.otp)
    }, [isOtpVerifySuccess])

    return (
        <>
            {/* Mobile Input */}
            <Heading fontSize="md">{title}</Heading>
            <Flex flexDirection={{ base: 'column', md: 'row' }} mt={6} w="full">
                <Flex flexGrow={1} maxW={{ md: 383 }}>
                    <Flex className="select-wrapper">
                        <Field
                            name={'countryCode'}
                            component={FormikSelect}
                            options={countryCodes.map(countryCode => ({
                                value: countryCode.code,
                                label: countryCode.code,
                            }))}
                            focusBorderColor="none"
                            useBasicStyles
                            isSearchable={false}
                            chakraStyles={{
                                control: provided => ({
                                    ...provided,
                                    boxShadow: 'none',
                                    borderRadius: 0,
                                    borderColor: 'mineshaft',
                                    border: defaultBorder,
                                    borderRight: 0,
                                    _hover: {
                                        borderColor: 'mineshaft',
                                    },
                                    _focusVisible: {
                                        boxShadow: '0 0 0 2px #3182ce',
                                    },
                                    height: 16,
                                    cursor: 'pointer',
                                    width: { base: 92, md: 100 },
                                    _disabled: {
                                        borderColor: isOtpVerifySuccess
                                            ? 'silver2'
                                            : 'mineshaft',
                                        color: 'silver',
                                    },
                                }),
                                valueContainer: provided => ({
                                    ...provided,
                                    padding: '0 0 0 21px',
                                    paddingInlineStart: '21px',
                                }),
                                singleValue: provided => ({
                                    ...provided,
                                    margin: 0,
                                    textOverflow: 'unset',
                                }),
                                dropdownIndicator: provided => ({
                                    ...provided,
                                    marginLeft: 2,
                                    marginRight: 4,
                                    height: 5,
                                    width: 5,
                                }),
                                menuList: provided => ({
                                    ...provided,
                                    minW: 0,
                                    maxW: { md: 420 },
                                    borderRadius: 0,
                                    border: '3px solid #575757',
                                    py: 0,
                                }),
                                option: (provided, state) => {
                                    return {
                                        ...provided,
                                        padding: {
                                            base: '1rem 1.5rem',
                                            md: '1rem 1.125rem',
                                        },
                                        _hover: {
                                            backgroundColor: 'wisppink',
                                        },
                                        backgroundColor: 'white',
                                        color: 'inherit',
                                        fontWeight: state.isSelected
                                            ? 'bold'
                                            : 'normal',
                                        fontSize: 'sm',
                                    }
                                },
                                indicatorsContainer: provided => ({
                                    ...provided,
                                    height: '24px',
                                    borderRight: '1px solid #cccccc',
                                    alignSelf: 'initial',
                                }),
                            }}
                            border={defaultBorder}
                            isDisabled
                        />
                    </Flex>
                    <NumberInputControl
                        name={'mobile'}
                        numberInputProps={{
                            zIndex: 1,
                            height: 16,
                            maxWidth: { md: 283 },
                            width: 'full',
                            inputMode: 'tel',
                            sx: {
                                input: {
                                    h: 'full',
                                    borderRadius: 0,
                                    border: defaultBorder,
                                    borderLeft: 'none',
                                    _hover: {
                                        border: defaultBorder,
                                        borderLeft: 'none',
                                    },
                                    _focusVisible: {
                                        border: defaultBorder,
                                        borderLeft: 'none',
                                        boxShadow: '0 0 0 2px #3182ce',
                                    },
                                    _disabled: {
                                        borderColor: 'silver2',
                                        opacity: 1,
                                        color: 'silver2',
                                    },
                                    paddingLeft: { base: 4, md: 6 },
                                },
                            },
                            isDisabled: isOtpVerifySuccess,
                            isValidCharacter: character =>
                                character.match(/^[0-9]$/),
                        }}
                        showStepper={false}
                        border={'none'}
                        outline={'none'}
                    />
                </Flex>
                {!isOtpVerifySuccess && (
                    <Button
                        variant="pressable"
                        width={{ base: 'full', md: 121 }}
                        mt={{ base: 4, md: 0 }}
                        height={16}
                        isDisabled={
                            values.mobile.length !== 10 ||
                            isOtpSendError ||
                            isOtpSendSuccess
                        }
                        _disabled={{
                            _active: {
                                boxShadow: 'none',
                            },
                            bgColor: 'alto2 !important',
                            color: 'silver',
                            cursor: 'not-allowed',
                        }}
                        borderLeft={{ base: defaultBorder, md: 'none' }}
                        padding={'unset'}
                        onClick={sendOtp}
                    >
                        Get OTP
                    </Button>
                )}
            </Flex>
            {isOtpSendSuccess && !isOtpVerifySuccess && (
                <>
                    {/* OTP Input */}
                    <Text mt={6} color="mineshaft" fontSize={'sm'}>
                        Enter the OTP sent to your mobile
                    </Text>
                    <PinInputControl
                        marginTop={6}
                        name="otp"
                        pinAmount={6}
                        isRequired
                        pinInputProps={{
                            variant: { base: 'retro-mobile-2', md: 'retro2' },
                            placeholder: '',
                            size: { base: 'lg', md: '2xl' },
                            otp: true,
                            type: 'number',
                            onComplete: () => {
                                setShouldVerifyOtp(true)
                            },
                            isInvalid: isOtpVerifyError,
                        }}
                        stackProps={{
                            gap: { base: 2, md: 0 },
                        }}
                    />
                    <Flex
                        w="full"
                        maxW={504}
                        justifyContent={'space-between'}
                        alignItems={'center'}
                        mt={{ base: 4, md: 8 }}
                    >
                        {remTime > 0 ? (
                            <Text color="rum" fontStyle={'italic'}>
                                Resend OTP in {remTime / 1000} secs.
                            </Text>
                        ) : (
                            <Text
                                fontStyle={'italic'}
                                color="rum"
                                textDecoration="underline"
                                cursor="pointer"
                                onClick={sendOtp}
                            >
                                Resend OTP
                            </Text>
                        )}
                        {isOtpVerifyError && (
                            <Text
                                fontStyle={'italic'}
                                color="flushmahogany"
                                fontSize={'sm'}
                            >
                                {otpVerifyError.message}
                            </Text>
                        )}
                    </Flex>
                </>
            )}
            {isOtpSendError && (
                <Text
                    mt={2}
                    color="flushmahogany"
                    fontSize={'sm'}
                    fontStyle={'italic'}
                >
                    {otpSendError.message}
                </Text>
            )}
            {isOtpVerifySuccess && (
                <Text
                    mt={6}
                    color="fruitsalad"
                    fontSize={'sm'}
                    fontStyle={'italic'}
                >
                    Mobile number successfully verified!
                </Text>
            )}
        </>
    )
}

export default VerifyMobileForm
